import { getPortfolios } from 'lib/api/integrations/digitalSignature';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import HeaderWithActions, {
  type CustomButtonProps,
} from 'lib/designSystem/HeaderWithActions';
import TableWithPagination from 'lib/designSystem/TableWithPagination';
import { errorHandler } from 'lib/functions/errors';
import { showAlert } from 'lib/functions/utils';
import { Portfolio } from 'lib/types/models';
import { Pagination } from 'lib/types/shared';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import CreatePortfolio from './Components/CreatePortfolio';
import PortfolioModal from './Components/PortfolioModal';

const BASE_PAGINATION = {
  page: 1,
  pageSize: 25,
  total: 0,
  totalPages: 0,
};

const Portafolios = () => {
  const dispatch = useDispatch();
  const { id: businessIdentifier } = useParams<{
    id: string;
    businessId: string;
    page: string;
  }>();
  const [portfolios, setPortfolios] = useState<Portfolio[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState<Pagination>(BASE_PAGINATION);
  const [openCreatePortfolio, setOpenCreatePortfolio] = useState(false);
  const [openPortfolioModal, setOpenPortfolioModal] = useState(false);
  const [portfolio, setPortfolio] = useState<Portfolio | null>(null);

  const tableHeaders = [
    { label: 'Título', key: 'title', type: 'text' },
    { label: 'Estado', key: 'status', type: 'status-chip' },
    { label: 'Operación', key: 'requestType', type: 'text' },
    { label: 'ID operación', key: 'requestId', type: 'text' },
    { label: 'Cantidad de Firmantes', key: 'signersQuantity', type: 'text' },
    { label: 'Cantidad de Documentos', key: 'documentsQuantity', type: 'text' },
    { label: 'Fecha de Creación', key: 'createdAt', type: 'date' },
    { label: 'Fecha de Actualización', key: 'updatedAt', type: 'date' },
    { label: 'Validaciones', key: 'validationsText', type: 'text' },
    { label: 'Válido hasta', key: 'validUntil', type: 'date' },
  ];

  const buttons: CustomButtonProps[] = [
    {
      name: 'Nuevo Portafolio',
      color: 'primary',
      action: () => setOpenCreatePortfolio(true),
    },
  ];

  const fetchPortfolios = async ({
    page,
    pageSize,
  }: {
    page: number;
    pageSize: number;
  }) => {
    try {
      setLoading(true);
      if (!businessIdentifier) {
        return showAlert(dispatch, 'error', 'Business identifier is required');
      }
      const query = `page=${page}&pageSize=${pageSize}`;
      const response = await getPortfolios(dispatch, businessIdentifier, query);
      setPortfolios(response.data);
      setPagination(response.pagination);
    } catch (error) {
      errorHandler(dispatch, error);
    } finally {
      setLoading(false);
    }
  };

  const handleClickRowPortfolio = (id: string) => {
    setOpenPortfolioModal(true);
    setPortfolio(portfolios.find((p) => p.id === id) || null);
  };

  useEffect(() => {
    void fetchPortfolios({ page: 1, pageSize: 10 });
  }, []);

  useEffect(() => {
    if (!openPortfolioModal || !portfolio) return;
    const updated = portfolios.find((p) => p.id === portfolio.id);
    if (updated) setPortfolio(updated);
  }, [portfolios, openPortfolioModal]);

  if (loading) return <CenteredCircularProgress />;

  return (
    <>
      <HeaderWithActions
        variant="body1"
        title="Acá podrás ver todos los portafolios de la empresa."
        buttons={buttons}
      />
      <TableWithPagination
        data={portfolios}
        setData={setPortfolios}
        dataHeaders={tableHeaders}
        showActions
        pagination={pagination}
        setPagination={setPagination}
        searchData={fetchPortfolios}
        setLoading={setLoading}
        exportToFile="xlsx"
        exportComplete
        handleClickRow={handleClickRowPortfolio}
        lastElement
      />
      <CreatePortfolio
        open={openCreatePortfolio}
        setOpen={setOpenCreatePortfolio}
        businessIdentifier={businessIdentifier}
        fetchPortfolios={fetchPortfolios}
      />
      <PortfolioModal
        open={openPortfolioModal}
        onClose={() => setOpenPortfolioModal(false)}
        portfolio={portfolio}
        onPortfolioUpdated={() =>
          void fetchPortfolios({ page: 1, pageSize: 10 })
        }
      />
    </>
  );
};

export default Portafolios;
